import { useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_WEIGHTS,
  SCORING_FIELD_KEYS,
  type MarketSegmentWithScore,
  type ScoringFieldKey,
  type Weights,
} from '@ispot/shared'
import type { useMarketSegmentation } from '@/hooks/useMarketSegmentation'
import { HeatmapTable, type HeatmapRow } from '../overview/HeatmapTable'
import { OverviewToolbar } from '../overview/OverviewToolbar'

type HookState = ReturnType<typeof useMarketSegmentation>

interface Props {
  state: HookState
  onOpenSegment?: (id: string) => void
}

const VIS_STORAGE_KEY = 'market-segmentation.visible-criteria'

export function OverviewTab({ state, onOpenSegment }: Props) {
  const weights = state.canvas?.weights ?? DEFAULT_WEIGHTS
  const [draft, setDraft] = useState<Weights | null>(null)
  const [confirmBeachhead, setConfirmBeachhead] = useState<HeatmapRow | null>(null)
  const [visibleCriteria, setVisibleCriteria] = useState<Set<ScoringFieldKey>>(() =>
    loadVisibleCriteria(),
  )
  const [scoringId, setScoringId] = useState<string | null>(null)
  const [bulkRunning, setBulkRunning] = useState(false)

  useEffect(() => {
    saveVisibleCriteria(visibleCriteria)
  }, [visibleCriteria])

  const effective = draft ?? weights

  const ranked = useMemo<HeatmapRow[]>(() => {
    const scored = state.segments.map((s) => ({
      ...s,
      liveScore: computeScore(s, effective, visibleCriteria),
    }))
    return scored.sort((a, b) => b.liveScore - a.liveScore)
  }, [state.segments, effective, visibleCriteria])

  const stats = useMemo(() => {
    const scored = ranked.filter((s) => s.liveScore > 0)
    const avg = scored.length
      ? scored.reduce((acc, s) => acc + s.liveScore, 0) / scored.length
      : 0
    return { total: state.segments.length, scored: scored.length, avg }
  }, [ranked, state.segments.length])

  const commitWeights = () => {
    if (!draft) return
    const next = draft
    setDraft(null)
    void state.updateWeights(next).catch(() => {})
  }

  const handleWeight = (key: ScoringFieldKey, value: number) => {
    setDraft({ ...effective, [key]: value })
  }

  const handleResetWeights = () => {
    setDraft(null)
    void state.updateWeights(DEFAULT_WEIGHTS).catch(() => {})
  }

  const handleToggleCriterion = (key: ScoringFieldKey) => {
    setVisibleCriteria((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        if (next.size === 1) return next // don't allow hiding all
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleScoreOne = async (seg: HeatmapRow) => {
    setScoringId(seg.id)
    try {
      await state.generateScoring(seg.id)
    } catch {
      /* swallow; hook surfaces error via reload */
    } finally {
      setScoringId(null)
    }
  }

  const handleScoreAll = async () => {
    setBulkRunning(true)
    for (const seg of state.segments) {
      setScoringId(seg.id)
      try {
        await state.generateScoring(seg.id)
      } catch {
        // continue with the next segment
      }
    }
    setScoringId(null)
    setBulkRunning(false)
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className="flex shrink-0 items-start justify-between gap-6 border-b px-8 py-5"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <div className="flex items-end gap-6">
          <Stat label="Segments" value={String(stats.total)} />
          <Stat label="Scored" value={String(stats.scored)} />
          <Stat
            label="Avg. score"
            value={stats.avg === 0 ? '—' : stats.avg.toFixed(1)}
          />
        </div>
        <OverviewToolbar
          weights={effective}
          dirty={!!draft}
          visibleCriteria={visibleCriteria}
          aiRunning={bulkRunning}
          canGenerate={state.segments.length > 0}
          onWeightChange={handleWeight}
          onWeightCommit={commitWeights}
          onWeightReset={handleResetWeights}
          onToggleCriterion={handleToggleCriterion}
          onGenerateAll={handleScoreAll}
        />
      </div>

      <div className="flex-1 overflow-auto px-8 py-6">
        {ranked.length === 0 ? (
          <EmptyState />
        ) : (
          <HeatmapTable
            rows={ranked}
            beachheadId={state.canvas?.beachheadSegmentId ?? null}
            visibleCriteria={visibleCriteria}
            scoringId={scoringId}
            personas={state.personas}
            coverage={state.coverage}
            onToggleBeachhead={(seg) => {
              if (seg.id === state.canvas?.beachheadSegmentId) {
                void state.setBeachhead(null).catch(() => {})
              } else {
                setConfirmBeachhead(seg)
              }
            }}
            onScoreWithAI={handleScoreOne}
            onOpenSegment={onOpenSegment ? (seg) => onOpenSegment(seg.id) : undefined}
          />
        )}
      </div>

      {confirmBeachhead ? (
        <BeachheadConfirmDialog
          segment={confirmBeachhead}
          onCancel={() => setConfirmBeachhead(null)}
          onConfirm={async () => {
            await state.setBeachhead(confirmBeachhead.id).catch(() => {})
            setConfirmBeachhead(null)
          }}
        />
      ) : null}
    </div>
  )
}

function EmptyState() {
  return (
    <div
      className="flex h-full items-center justify-center rounded-lg border border-dashed py-16 text-center"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="max-w-sm">
        <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
          No segments yet
        </h3>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          Add a segment on the Segments tab or upload documents — existing customer
          segments from your intelligence will appear here automatically.
        </p>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>{label}</div>
      <div className="font-display text-xl font-semibold" style={{ color: 'var(--color-text)' }}>{value}</div>
    </div>
  )
}

function BeachheadConfirmDialog({
  segment,
  onCancel,
  onConfirm,
}: {
  segment: MarketSegmentWithScore
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <div
        className="w-[420px] rounded-lg border p-5 shadow-lg"
        style={{
          backgroundColor: 'var(--color-bg, white)',
          borderColor: 'var(--color-border-subtle)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="font-display text-base font-semibold"
          style={{ color: 'var(--color-text)' }}
        >
          Set beachhead?
        </h3>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <strong>{segment.name}</strong> will become the beachhead segment — the one
          you're going after first. This can be changed at any time.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
          >
            Set beachhead
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Live weighted score. Uses only criteria currently visible on the heatmap,
 * so hiding irrelevant criteria also removes them from the ranking.
 */
function computeScore(
  seg: MarketSegmentWithScore,
  weights: Weights,
  visible: Set<ScoringFieldKey>,
): number {
  let total = 0
  let used = 0
  for (const key of SCORING_FIELD_KEYS) {
    if (!visible.has(key)) continue
    const field = seg.scoring[key]
    const w = weights[key] ?? 0
    if (field?.value == null) continue
    total += field.value * w
    used += w
  }
  if (used === 0) return 0
  return Math.round((total / used) * 10) / 10
}

function loadVisibleCriteria(): Set<ScoringFieldKey> {
  try {
    const raw = localStorage.getItem(VIS_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as string[]) : []
    const valid = parsed.filter((k): k is ScoringFieldKey =>
      (SCORING_FIELD_KEYS as readonly string[]).includes(k),
    )
    return valid.length > 0 ? new Set(valid) : new Set(SCORING_FIELD_KEYS)
  } catch {
    return new Set(SCORING_FIELD_KEYS)
  }
}

function saveVisibleCriteria(set: Set<ScoringFieldKey>): void {
  try {
    localStorage.setItem(VIS_STORAGE_KEY, JSON.stringify(Array.from(set)))
  } catch {
    /* ignore */
  }
}
